import { useQuery, keepPreviousData, UseQueryOptions } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";

// Types
export interface GrupoTableType {
  id: number;
  codigo: string;
  descricao: string;
  ativo: boolean;
  permissoes: string[];
  dataCriacao: string;
  dataAlteracao?: string;
  dataExclusao?: string;
  usuarioCriacao: string;
  usuarioAlteracao?: string;
  usuarioExclusao?: string;
}

// Updated to match PageResult<T> from backend
export interface GrupoApiResponse {
  data: GrupoTableType[];
  isSuccess: boolean;
  message: string;
  total: number;
  pageNumber: number;
  pageSize: number;
}

// Interface para os filtros ativos
export interface ActiveFilters {
  groupStatus: string; // 'active', 'deleted', 'all'
}

// Fetch function for grupos
export const fetchGrupos = async (
  filter?: string,
  filters?: ActiveFilters,
  pageIndex: number = 1,
  pageSize: number = 10
): Promise<GrupoApiResponse> => {
  // Determinar se incluir grupos excluídos
  const incluirExcluidos =
    filters?.groupStatus === "all" || filters?.groupStatus === "deleted";
  // Determinar se filtrar para mostrar apenas excluídos
  const apenasExcluidos = filters?.groupStatus === "deleted";

  const response = await axios.get<GrupoApiResponse>("/api/grupo", {
    params: {
      ...(filter && { filter }),
      incluirExcluidos,
      apenasExcluidos,
      pageIndex,
      pageSize,
    },
  });

  return response.data;
};

// Hook para obter grupos
export const useGrupos = (
  filter: string = "",
  filters: ActiveFilters = { groupStatus: "active" },
  pageIndex: number = 1,
  pageSize: number = 10,
  options?: UseQueryOptions<GrupoApiResponse>
) => {
  const { getQueryRetryHandler } = usePermissionModal();
  
  return useQuery<GrupoApiResponse>({
    queryKey: ["grupos", filter, filters, pageIndex, pageSize],
    queryFn: () => fetchGrupos(filter, filters, pageIndex, pageSize),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 30000, // Dados válidos por 30 segundos antes de revalidar
    retry: getQueryRetryHandler({
      queryKey: ["grupos", filter, filters, pageIndex, pageSize],
      defaultMessage: "Você não possui permissão para consultar grupos de acesso."
    }),
    ...options
  });
};
